import { EChartsInstance } from 'echarts-for-react'
import styles from './VideoAnalysisChartCover.module.less'
import { useChartConfig } from '../CoreChart/CoreChart'
import { useCallback, useContext, useEffect, useState } from 'react'
import { ProjectDetailPageContext } from '@/Contexts/Contexts'
import cls from 'classnames'

function toFixedNum(num: number, precision = 2): number {
  return parseFloat(num.toFixed(precision))
}

interface Props {
  chartInstance: EChartsInstance | null
  project: ProjectModels.ProjectDetail | ProjectModels.ProjectDetailV3
  orientation?: 'landscape' | 'portrait'
  onRendered?: () => void
  style?: React.CSSProperties
  thumbnailMode?: 'default' | 'portrait-splash' | 'portrait-ppt' | 'portrait-pdf'
}

function VideoAnalysisChartCover({
  chartInstance,
  project,
  orientation = 'landscape',
  onRendered,
  style,
  thumbnailMode = 'default',
}: Props) {
  const { handleClickHeatMap } = useContext(ProjectDetailPageContext)!
  const { config: chartConfig } = useChartConfig(project.detailMetrics)
  const [points, setPoints] = useState<
    { x: number; y: number; frame: string; secondCount: number }[]
  >([])
  const [views, setViews] = useState<Array<{ x: number; centerX: number; src: string }>>([])
  const [marginLeft, setMarginLeft] = useState(0)

  const calculatePoints = useCallback(() => {
    if (!chartInstance) {
      return
    }
    setTimeout(() => {
      const points = Array.from({ length: project.frameInfos.length }).map((_, index) => {
        const point = chartInstance.convertToPixel({ seriesIndex: 0 }, [index, 0])
        return {
          x: point?.[0],
          y: point?.[1],
          frame: project.detailMetrics[index].heatMap,
          secondCount: Number(project.frameInfos[index].secondCount),
        }
      })
      setPoints(points)
    })
  }, [chartInstance])

  useEffect(() => {
    calculatePoints()
    chartInstance?.on('rendered', () => {
      calculatePoints()
    })
  }, [chartInstance])

  useEffect(() => {
    if (
      thumbnailMode === 'portrait-splash' ||
      thumbnailMode === 'portrait-ppt' ||
      thumbnailMode === 'portrait-pdf'
    ) {
      setMarginLeft(0)
      return
    }
    const first = views[0]
    if (first && !isNaN(first.x)) {
      setMarginLeft(first.x)
    }
  }, [thumbnailMode, views])

  useEffect(() => {
    if (!points.length) {
      return
    }
    const heatmapFrames = project.detailMetrics.map((item) => item.heatMap)
    const views = heatmapFrames.map((frame, index) => {
      const xAxisOffset = points.length > 1 ? points[1].x - points[0].x : 0
      const width = xAxisOffset
      return {
        // 按照设计稿，这里要往前挪一半 x 的刻度
        x: toFixedNum(xAxisOffset * index) + (chartConfig.grid as any).left,
        centerX: 0,
        width,
        height: points[0].y,
        src: frame,
      }
    })
    setViews(views)
  }, [points])

  useEffect(() => {
    if (views.length === points.length && onRendered) {
      onRendered()
    }
  }, [views, points])

  return (
    <div
      className={cls(
        styles.videoAnalysisChartCover,
        thumbnailMode === 'portrait-ppt' && styles.videoAnalysisChartCoverPortraitPpt,
        thumbnailMode === 'portrait-pdf' && styles.videoAnalysisChartCoverPortraitPdf
      )}
      style={{
        ...style,
        marginLeft: style?.marginLeft ?? marginLeft,
        overflow: 'visible',
        // width: `calc(100% - ${(chartConfig.grid as any).left + (chartConfig.grid as any).right}px)`,
      }}
    >
      {views.map((view, index) => {
        return (
          <div
            key={view.src}
            className={cls(
              styles.item,
              thumbnailMode === 'portrait-ppt' && styles.itemPortraitPpt
            )}
            style={{
              flex:
                thumbnailMode === 'portrait-splash' ||
                thumbnailMode === 'portrait-ppt' ||
                thumbnailMode === 'portrait-pdf'
                  ? `0 0 ${100 / views.length}%`
                  : 1,
            }}
          >
            <img
              className={cls(
                styles.cover,
                orientation === 'portrait' && styles.coverPortrait,
                thumbnailMode === 'portrait-splash' && styles.coverPortraitSplash,
                thumbnailMode === 'portrait-ppt' && styles.coverPortraitPpt,
                thumbnailMode === 'portrait-pdf' && styles.coverPortraitPdf
              )}
              key={view.src}
              src={view.src}
              style={{
                width:
                  thumbnailMode === 'portrait-ppt' || thumbnailMode === 'portrait-pdf'
                    ? 'auto'
                    : '100%',
              }}
              onClick={() => {
                handleClickHeatMap(index)
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

export default VideoAnalysisChartCover
